import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { AuthResponse, AuthService } from './auth/auth.service';
import { AuthShellComponent } from './auth-shell/auth-shell.component';
import { FeatureInfoPanelDataService } from './feature-info-panel/feature-info-panel-data.service';
import { FeatureInfoPanelComponent, GuardTakeStatusChangeRequest } from './feature-info-panel/feature-info-panel.component';
import { GuardFieldOperationsComponent } from './guard-field-operations/guard-field-operations.component';
import { GuardFieldElement, GuardFieldElementType, GuardFieldOperationsView, GuardFieldPoint } from './guard-field-operations.service';
import { GuardExtentService } from './guard-extent.service';
import { GuardTake, GuardTakesService } from './guard-takes.service';
import { IrrigatorExtentService } from './irrigator-extent.service';
import { IrrigatorAccessOverlaysComponent } from './irrigator-access-overlays/irrigator-access-overlays.component';
import { IrrigatorParcel, IrrigatorParcelsService } from './irrigator-parcels.service';
import { LayerControlComponent } from './layer-control/layer-control.component';
import { MobileMenuComponent } from './mobile-menu/mobile-menu.component';
import { PublicAccessOverlaysComponent } from './public-access-overlays/public-access-overlays.component';
import { CADASTRAL_REFERENCE_QUERY_PARAMETER, createRamLayerOptions, RAM_ORTHOPHOTO_LAYER_KEY } from './ram-layer.config';
import { RamFeatureInfoItem, RamWmsService } from './ram-wms.service';
import { SidebarMenuComponent } from './sidebar-menu/sidebar-menu.component';
import { SiteFooterComponent } from './site-footer/site-footer.component';
import { TopbarComponent } from './topbar/topbar.component';
import { LOGIN_OPTIONS, PROFILE_ICON_PATHS, PROFILE_THUMBNAIL_PATHS } from './viewer-profile.config';
import { ViewerStateService } from './viewer-state.service';
import { AuthenticatedProfileKey, FeatureInfoPanelData, LoginOption, ProfileKey, RamLayerOption } from './visor.types';

declare global {
  interface Window {
    ol?: any;
  }
}

interface FeatureInfoQuery {
  label: string;
  proxyKey: string;
  source: any;
}

@Component({
  selector: 'app-root',
  imports: [
    AuthShellComponent,
    FeatureInfoPanelComponent,
    GuardFieldOperationsComponent,
    IrrigatorAccessOverlaysComponent,
    LayerControlComponent,
    MobileMenuComponent,
    PublicAccessOverlaysComponent,
    SidebarMenuComponent,
    SiteFooterComponent,
    TopbarComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private static readonly PARCELAS_EXTENT: [number, number, number, number] = [
    -0.553178270185069,
    39.44291699118916,
    -0.2576211261296409,
    39.644399167295504
  ];
  private static readonly MAP_PAN_EXTENT: [number, number, number, number] = [
    -0.68,
    39.395,
    -0.13,
    39.69
  ];
  private static readonly MAX_DETAIL_RESOLUTION = 0.5125706184341107;
  private static readonly IRRIGATOR_AUTO_FIT_MIN_RESOLUTION = AppComponent.MAX_DETAIL_RESOLUTION * 2.2;
  private static readonly CADASTRAL_REFERENCE_SEARCH_DELAY_MS = 300;

  @ViewChild('mapHost') private mapHost?: ElementRef<HTMLDivElement>;

  private readonly authService = inject(AuthService);
  private readonly featureInfoPanelDataService = inject(FeatureInfoPanelDataService);
  private readonly guardExtentService = inject(GuardExtentService);
  private readonly guardTakesService = inject(GuardTakesService);
  private readonly irrigatorExtentService = inject(IrrigatorExtentService);
  private readonly irrigatorParcelsService = inject(IrrigatorParcelsService);
  private readonly ramWmsService = inject(RamWmsService);
  private readonly viewerState = inject(ViewerStateService);

  protected readonly title = 'Visor preliminar';
  protected readonly profileIconPaths = PROFILE_ICON_PATHS;
  protected readonly profileThumbnailPaths = PROFILE_THUMBNAIL_PATHS;
  protected readonly loginOptions = LOGIN_OPTIONS;
  protected readonly ramLayers: RamLayerOption[] = createRamLayerOptions();

  protected get showCertificateCallback(): boolean { return this.viewerState.showCertificateCallback; }
  protected get showLogin(): boolean { return this.viewerState.showLogin; }
  protected get showIrrigatorAuth(): boolean { return this.viewerState.showIrrigatorAuth; }
  protected get showGuardAuth(): boolean { return this.viewerState.showGuardAuth; }
  protected get showManagerAuth(): boolean { return this.viewerState.showManagerAuth; }
  protected get certificateCallbackError(): string { return this.viewerState.certificateCallbackError; }
  protected get certificateCallbackLoading(): boolean { return this.viewerState.certificateCallbackLoading; }
  protected get currentProfile(): ProfileKey { return this.viewerState.currentProfile; }
  protected get currentAccessTitle(): string { return this.viewerState.currentAccessTitle; }
  protected get currentAccessSubtitle(): string { return this.viewerState.currentAccessSubtitle; }
  protected get status(): string { return this.viewerState.status; }
  protected set status(value: string) { this.viewerState.status = value; }
  protected get libraryReady(): boolean { return this.viewerState.libraryReady; }
  protected set libraryReady(value: boolean) { this.viewerState.libraryReady = value; }
  protected get popupVisible(): boolean { return this.viewerState.popupVisible; }
  protected get featureInfoPanelData(): FeatureInfoPanelData | undefined { return this.viewerState.featureInfoPanelData; }
  protected get publicInfoVisible(): boolean { return this.viewerState.publicInfoVisible; }
  protected get layersPanelVisible(): boolean { return this.viewerState.layersPanelVisible; }
  protected set layersPanelVisible(value: boolean) { this.viewerState.layersPanelVisible = value; }
  protected get aboutRamVisible(): boolean { return this.viewerState.aboutRamVisible; }
  protected get irrigatorParcelsPanelVisible(): boolean { return this.viewerState.irrigatorParcelsPanelVisible; }
  protected get cadastralReferenceSearch(): string { return this.viewerState.cadastralReferenceSearch; }
  protected set cadastralReferenceSearch(value: string) { this.viewerState.cadastralReferenceSearch = value; }
  protected get mobileMenuVisible(): boolean { return this.viewerState.mobileMenuVisible; }

  private map?: any;
  private cadastralReferenceSearchTimeout?: number;
  private hasFitIrrigatorExtent = false;
  private hasFitGuardExtent = false;
  protected irrigatorParcels: IrrigatorParcel[] = [];
  protected irrigatorParcelsLoading = false;
  protected irrigatorParcelsError = '';
  protected selectedIrrigatorParcelId = '';
  private irrigatorParcelsLoaded = false;
  protected guardTakesPanelVisible = true;
  protected guardTakes: GuardTake[] = [];
  protected guardTakesLoading = false;
  protected guardTakesError = '';
  protected selectedGuardTakeId = '';
  protected guardTakeStatusSaving = false;
  protected guardTakeStatusError = '';
  protected guardFieldOperationsView: GuardFieldOperationsView = null;
  protected incidentsSummaryVisible = false;
  protected maintenanceSummaryVisible = false;
  protected guardFieldPointSelectionActive = false;
  protected guardGeolocationLoading = false;
  protected guardGeolocationError = '';
  protected guardSelectedMapPoint?: GuardFieldPoint;
  protected guardDetectedMapElements: GuardFieldElement[] = [];
  protected deploymentNoticeVisible = true;
  protected featureInfoActive = false;
  private guardTakesLoaded = false;
  private guardFieldPointLayer?: any;
  private readonly handlePopState = (): void => {
    this.viewerState.syncRoute(window.location.pathname);

    if (this.showCertificateCallback) {
      void this.completeCertificateLogin();
      return;
    }

    if (!this.showLogin) {
      if (window.location.pathname === '/') {
        this.setPublicProfile();
      }
      this.initializeMapSoon();
    }
  };

  ngAfterViewInit(): void {
    window.addEventListener('popstate', this.handlePopState);

    if (this.showCertificateCallback) {
      void this.completeCertificateLogin();
      return;
    }

    this.restoreStoredSession();

    if (this.showLogin) {
      return;
    }

    this.initializeMapSoon();
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.handlePopState);
    window.clearTimeout(this.cadastralReferenceSearchTimeout);
    this.map?.setTarget(undefined);
  }

  protected enterAs(option: LoginOption): void {
    if (option.key === 'irrigators') {
      this.viewerState.showIrrigatorLogin();
      return;
    }

    if (option.key === 'guards') {
      this.viewerState.showGuardLogin();
      return;
    }

    this.viewerState.showManagerLogin();
  }

  protected backToProfileSelection(): void {
    this.viewerState.backToProfileSelection();
  }

  protected closeDeploymentNotice(): void {
    this.deploymentNoticeVisible = false;
  }

  protected returnToIrrigatorAuth(): void {
    this.viewerState.returnToIrrigatorAuth();
    window.history.replaceState({}, '', '/perfiles');
  }

  protected startIrrigatorSession(authResponse: AuthResponse): void {
    this.startSessionFromAuthResponse(authResponse, 'irrigators');
  }

  protected startAuthenticatedSessionFromResponse(authResponse: AuthResponse): void {
    const profileKey = this.getProfileKeyFromAuthProfile(authResponse.profile);

    if (!profileKey) {
      return;
    }

    this.startSessionFromAuthResponse(authResponse, profileKey);
  }

  private startSessionFromAuthResponse(authResponse: AuthResponse, profileKey: AuthenticatedProfileKey): void {
    const option = this.loginOptions.find((loginOption) => loginOption.key === profileKey);

    if (!option) {
      return;
    }

    this.authService.persistAuthResponse(authResponse);
    this.startAuthenticatedSession(option);
  }

  private startAuthenticatedSession(option: LoginOption): void {
    this.featureInfoActive = false;
    this.viewerState.startAuthenticatedSession(option);
    window.history.pushState({}, '', '/visor');
    this.initializeMapSoon();
  }

  private restoreStoredSession(): void {
    if (!this.authService.hasStoredAccessToken()) {
      return;
    }

    if (this.authService.isStoredTokenExpired()) {
      this.authService.clearAuthResponse();
      return;
    }

    if (this.showLogin) {
      return;
    }

    const storedProfileKey = this.getProfileKeyFromAuthProfile(this.authService.getStoredProfile());
    if (storedProfileKey) {
      const option = this.loginOptions.find((loginOption) => loginOption.key === storedProfileKey);
      if (option) {
        this.viewerState.restoreAuthenticatedProfile(option);
      }
    }
  }

  private getProfileKeyFromAuthProfile(profile: string | null): AuthenticatedProfileKey | undefined {
    if (profile === 'irrigator') {
      return 'irrigators';
    }

    if (profile === 'guard') {
      return 'guards';
    }

    if (profile === 'manager') {
      return 'managers';
    }

    return undefined;
  }


  private async completeCertificateLogin(): Promise<void> {
    if (this.certificateCallbackLoading) {
      return;
    }

    const callbackUrl = new URL(window.location.href);
    const code = callbackUrl.searchParams.get('code');
    const error = callbackUrl.searchParams.get('error');

    this.viewerState.showLogin = true;
    this.viewerState.showCertificateCallback = true;
    this.viewerState.showIrrigatorAuth = false;
    this.viewerState.showManagerAuth = false;
    this.viewerState.certificateCallbackError = '';

    if (error) {
      this.viewerState.certificateCallbackError = this.getCertificateCallbackErrorMessage(error);
      this.viewerState.certificateCallbackLoading = false;
      return;
    }

    if (!code) {
      this.viewerState.certificateCallbackError = 'No se ha recibido el código de autenticación del certificado.';
      this.viewerState.certificateCallbackLoading = false;
      return;
    }

    this.viewerState.certificateCallbackLoading = true;

    try {
      const response = await this.authService.exchangeCertificateLoginCode(code);

      if (!response.ok) {
        this.viewerState.certificateCallbackError = response.status === 401
          ? 'El código de autenticación ha caducado o ya ha sido utilizado.'
          : 'No se ha podido completar el acceso con certificado.';
        return;
      }

      this.startIrrigatorSession(await response.json() as AuthResponse);
    } catch {
      this.viewerState.certificateCallbackError = 'No se ha podido contactar con el servicio de autenticación.';
    } finally {
      this.viewerState.certificateCallbackLoading = false;
    }
  }

  private getCertificateCallbackErrorMessage(error: string): string {
    if (error === 'pending_payments') {
      return 'El comunero tiene pagos pendientes y eso impide que acceda al sistema.';
    }

    if (error === 'ambiguous_certificate') {
      return 'El certificado coincide con mas de un comunero. Contacta con la Real Acequia de Moncada.';
    }

    return 'No se ha podido validar el certificado digital para este comunero.';
  }

  protected returnToLogin(event: Event): void {
    event.preventDefault();
    if (this.isPublicAccess()) {
      return;
    }
    this.closeAuthenticatedSession('Sesión cerrada.');
  }

  protected isPublicAccess(): boolean {
    return this.viewerState.isPublicAccess();
  }

  protected isIrrigatorAccess(): boolean {
    return this.viewerState.isIrrigatorAccess();
  }

  protected isGuardAccess(): boolean {
    return this.viewerState.isGuardAccess();
  }

  protected isManagerAccess(): boolean {
    return this.viewerState.isManagerAccess();
  }

  protected getUserInitials(): string {
    if (this.currentProfile === 'irrigators') {
      return 'RM';
    }

    if (this.currentProfile === 'guards') {
      return 'GM';
    }

    return 'GM';
  }

  protected getProfileIconPath(profile: ProfileKey = this.currentProfile): string {
    return this.profileIconPaths[profile];
  }

  protected getProfileThumbnailPath(profile: ProfileKey = this.currentProfile): string {
    return this.profileThumbnailPaths[profile];
  }

  protected openProfileSelection(event: Event): void {
    event.preventDefault();
    this.featureInfoActive = false;
    this.viewerState.openProfileSelection();
    this.closePopup();
    this.map?.setTarget(undefined);
    this.map = undefined;
    this.hasFitIrrigatorExtent = false;
    this.hasFitGuardExtent = false;
    this.resetIrrigatorParcels();
    this.resetGuardTakes();
    this.resetGuardFieldOperations();
    window.history.pushState({}, '', '/perfiles');
  }

  private setPublicProfile(): void {
    this.featureInfoActive = false;
    this.viewerState.setPublicProfile();
  }

  protected togglePublicInformation(): void {
    this.viewerState.togglePublicInformation();
    this.refreshMapSize();
  }

  protected openMobileMenu(): void {
    this.viewerState.openMobileMenu();
  }

  protected closeMobileMenu(): void {
    this.viewerState.closeMobileMenu();
  }

  protected showAboutRam(): void {
    this.viewerState.toggleAboutRam();
    this.refreshMapSize();
  }

  protected closeAboutRam(): void {
    this.viewerState.closeAboutRam();
    this.refreshMapSize();
  }

  private closePublicPanels(): void {
    this.viewerState.closePublicPanels();
  }

  protected toggleIrrigatorParcelsPanel(): void {
    this.viewerState.toggleIrrigatorParcelsPanel();
    this.refreshMapSize();

    if (this.irrigatorParcelsPanelVisible) {
      void this.ensureIrrigatorParcelsLoaded();
    }
  }

  protected showIrrigatorParcelsPanel(): void {
    this.viewerState.showIrrigatorParcelsPanel();
    this.refreshMapSize();
    void this.ensureIrrigatorParcelsLoaded();
    this.focusIrrigatorParcels();
  }

  protected hideIrrigatorParcelsPanel(): void {
    this.viewerState.hideIrrigatorParcelsPanel();
    this.refreshMapSize();
  }

  private closeAuthenticatedSession(message = 'La sesión ha caducado. Vuelve a iniciar sesión.'): void {
    this.authService.clearAuthResponse();
    this.featureInfoActive = false;
    this.viewerState.closeAuthenticatedSession(message);
    this.closePopup();
    this.map?.setTarget(undefined);
    this.map = undefined;
    this.hasFitIrrigatorExtent = false;
    this.hasFitGuardExtent = false;
    this.resetIrrigatorParcels();
    this.resetGuardTakes();
    this.resetGuardFieldOperations();

    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }

    this.initializeMapSoon();
  }

  private initializeMapSoon(): void {
    window.setTimeout(() => this.initializeMap(), 0);
  }

  private initializeMap(): void {
    if (this.map) {
      this.refreshMapSize();
      return;
    }

    const ol = window.ol;

    if (!ol || !this.mapHost) {
      this.libraryReady = false;
      this.status = 'No se ha podido cargar OpenLayers desde CDN.';
      return;
    }

    const projectedParcelasExtent = ol.proj.transformExtent(
      AppComponent.PARCELAS_EXTENT,
      'EPSG:4326',
      'EPSG:3857'
    );
    const projectedPanExtent = ol.proj.transformExtent(
      AppComponent.MAP_PAN_EXTENT,
      'EPSG:4326',
      'EPSG:3857'
    );

    this.map = new ol.Map({
      target: this.mapHost.nativeElement,
      layers: [],
      view: new ol.View({
        center: ol.proj.fromLonLat([-0.3763, 39.5667]),
        zoom: 12,
        extent: projectedPanExtent,
        minResolution: AppComponent.MAX_DETAIL_RESOLUTION
      })
    });

    const view = this.map.getView();

    view.fit(projectedParcelasExtent, {
      padding: [40, 40, 48, 40],
      duration: 0
    });
    view.setMinZoom(view.getZoom() ?? 12);

    this.registerMapClickHandler();
    this.initializeRamLayers();
    void this.ensureIrrigatorParcelsLoaded();
    void this.ensureGuardTakesLoaded();
    void this.fitCurrentIrrigatorExtent();
    void this.fitCurrentGuardExtent();
    this.refreshMapSize();
  }

  protected toggleRamLayer(ramLayer: RamLayerOption, enabled: boolean): void {
    ramLayer.enabled = enabled;
    ramLayer.mapLayer?.setVisible(enabled);

    const activeLayers = this.getAvailableRamLayers()
      .filter((layer) => layer.enabled)
      .map((layer) => layer.label);

    this.status = activeLayers.length
      ? `Capas activas: ${activeLayers.join(', ')}`
      : 'No hay capas activas.';
  }

  protected zoomIn(): void {
    this.changeZoomBy(1);
  }

  protected zoomOut(): void {
    this.changeZoomBy(-1);
  }

  protected setFeatureInfoActive(active: boolean): void {
    this.featureInfoActive = active;

    if (active) {
      this.status = 'Consulta de información activada. Haz clic sobre el mapa.';
      return;
    }

    this.closePopup();
    this.status = 'Consulta de información desactivada.';
  }

  private changeZoomBy(delta: number): void {
    const view = this.map?.getView();
    const currentZoom = view?.getZoom();

    if (!view || currentZoom === undefined) {
      return;
    }

    view.animate({
      zoom: currentZoom + delta,
      duration: 180
    });
  }

  protected scheduleCadastralReferenceSearch(): void {
    window.clearTimeout(this.cadastralReferenceSearchTimeout);
    this.cadastralReferenceSearchTimeout = window.setTimeout(
      () => this.applyCadastralReferenceSearch(),
      AppComponent.CADASTRAL_REFERENCE_SEARCH_DELAY_MS
    );
  }

  private applyCadastralReferenceSearch(): void {
    const cadastralReference = this.getNormalizedCadastralReferenceSearch();

    this.getAvailableRamLayers()
      .filter((ramLayer) => ramLayer.supportsCadastralReferenceSearch && ramLayer.mapLayer)
      .forEach((ramLayer) => {
        ramLayer.mapLayer.getSource().updateParams({
          [CADASTRAL_REFERENCE_QUERY_PARAMETER]: cadastralReference
        });
      });

    this.status = cadastralReference
      ? `Filtrando parcelas por referencia catastral: ${cadastralReference}`
      : 'Filtro de referencia catastral eliminado.';
  }

  protected closePopup(): void {
    this.viewerState.closePopup();
    this.selectedIrrigatorParcelId = '';
    this.selectedGuardTakeId = '';
    this.refreshMapSize();
  }

  private initializeRamLayers(): void {
    if (!this.map || !window.ol) {
      return;
    }

    const ol = window.ol;

    this.getAvailableRamLayers().forEach((ramLayer, index) => {
      const isOrthophotoLayer = ramLayer.proxyKey === RAM_ORTHOPHOTO_LAYER_KEY;

      ramLayer.mapLayer = new ol.layer.Image({
        visible: ramLayer.enabled,
        opacity: isOrthophotoLayer ? 1 : 0.85,
        zIndex: isOrthophotoLayer ? 0 : 10 + index,
        source: new ol.source.ImageWMS({
          url: this.ramWmsService.getProxyWmsUrl(ramLayer.proxyKey),
          params: {
            FORMAT: 'image/png',
            TRANSPARENT: !isOrthophotoLayer,
            [CADASTRAL_REFERENCE_QUERY_PARAMETER]: ramLayer.supportsCadastralReferenceSearch
              ? this.getNormalizedCadastralReferenceSearch()
              : ''
          },
          imageLoadFunction: this.ramWmsService.createAuthenticatedImageLoadFunction(
            () => this.getAuthorizationHeaders(),
            () => this.closeAuthenticatedSession()
          ),
          ratio: 1
        })
      });

      this.map.addLayer(ramLayer.mapLayer);
    });
  }

  protected focusIrrigatorParcels(): void {
    void this.fitCurrentIrrigatorExtent(true);
  }

  protected selectIrrigatorParcel(parcel: IrrigatorParcel): void {
    this.selectedIrrigatorParcelId = parcel.id;
    this.viewerState.showFeatureInfoPanel(parcel.panelData);
    this.refreshMapSize();

    if (parcel.extent) {
      this.fitParcelExtent(parcel.extent);
      this.status = 'Información de parcela consultada.';
      return;
    }

    this.status = 'Información de parcela consultada, pero no se ha podido encuadrar automáticamente.';
  }

  private async ensureIrrigatorParcelsLoaded(force = false): Promise<void> {
    if (!this.isIrrigatorAccess() || this.irrigatorParcelsLoading || (!force && this.irrigatorParcelsLoaded)) {
      return;
    }

    this.irrigatorParcelsLoading = true;
    this.irrigatorParcelsError = '';

    const result = await this.irrigatorParcelsService.getCurrentParcels(this.getAuthorizationHeaders());

    this.irrigatorParcelsLoading = false;

    if (result.kind === 'unauthorized') {
      this.closeAuthenticatedSession();
      return;
    }

    if (result.kind === 'unavailable') {
      this.irrigatorParcelsError = 'No se han podido cargar tus parcelas.';
      this.status = this.irrigatorParcelsError;
      return;
    }

    this.irrigatorParcels = result.parcels;
    this.irrigatorParcelsLoaded = true;
    this.status = result.parcels.length
      ? `Parcelas cargadas: ${result.parcels.length}.`
      : 'No se han encontrado parcelas asociadas al comunero.';
  }

  private fitParcelExtent(extent: [number, number, number, number]): void {
    if (!this.map) {
      return;
    }

    this.map.getView().fit(this.getBufferedMapExtent(extent), {
      padding: [96, 420, 180, 96],
      duration: 550,
      minResolution: AppComponent.MAX_DETAIL_RESOLUTION
    });
  }

  protected toggleGuardTakesPanel(): void {
    this.guardTakesPanelVisible = !this.guardTakesPanelVisible;

    if (this.guardTakesPanelVisible) {
      void this.ensureGuardTakesLoaded();
    }
  }

  protected setGuardFieldOperationsView(view: GuardFieldOperationsView): void {
    this.guardFieldOperationsView = view;
    if (view === null) {
      this.incidentsSummaryVisible = false;
      this.maintenanceSummaryVisible = false;
      this.cancelGuardFieldPointSelection();
      this.resetGuardSelectedMapPoint();
    } else {
      if (view === 'incidents') {
        this.incidentsSummaryVisible = true;
      } else {
        this.maintenanceSummaryVisible = true;
      }
      this.deploymentNoticeVisible = false;
      this.guardFieldPointSelectionActive = false;
      this.featureInfoActive = false;
      this.viewerState.closePopup();
      this.viewerState.closeMobileMenu();
    }
    this.refreshMapSize();
  }

  protected toggleGuardFieldOperationsSummary(view: GuardFieldOperationsView): void {
    if (view === null) {
      this.setGuardFieldOperationsView(null);
      return;
    }

    const summaryVisible = view === 'incidents'
      ? this.incidentsSummaryVisible
      : this.maintenanceSummaryVisible;
    if (!summaryVisible) {
      this.setGuardFieldOperationsView(view);
      return;
    }

    if (view === 'incidents') {
      this.incidentsSummaryVisible = false;
    } else {
      this.maintenanceSummaryVisible = false;
    }

    if (this.guardFieldOperationsView === view) {
      this.guardFieldOperationsView = this.incidentsSummaryVisible
        ? 'incidents'
        : (this.maintenanceSummaryVisible ? 'maintenance' : null);
    }

    if (!this.incidentsSummaryVisible && !this.maintenanceSummaryVisible) {
      this.cancelGuardFieldPointSelection();
      this.resetGuardSelectedMapPoint();
    }
    this.refreshMapSize();
  }

  protected toggleMobileGuardFieldOperationsSummary(view: GuardFieldOperationsView): void {
    if (view === 'incidents') {
      this.maintenanceSummaryVisible = false;
    } else if (view === 'maintenance') {
      this.incidentsSummaryVisible = false;
    }

    this.toggleGuardFieldOperationsSummary(view);
  }

  protected setGuardFieldSummaryVisibility(
    change: { view: Exclude<GuardFieldOperationsView, null>; visible: boolean }
  ): void {
    if (change.view === 'incidents') {
      this.incidentsSummaryVisible = change.visible;
    } else {
      this.maintenanceSummaryVisible = change.visible;
    }
  }

  protected beginGuardFieldPointSelection(): void {
    if (!this.isGuardAccess()) {
      return;
    }

    this.guardFieldPointSelectionActive = true;
    this.guardGeolocationError = '';
    this.deploymentNoticeVisible = false;
    this.featureInfoActive = false;
    this.viewerState.closePopup();
    this.status = 'Selección de ubicación activada. Marca un punto en el mapa.';
  }

  protected cancelGuardFieldPointSelection(): void {
    this.guardFieldPointSelectionActive = false;
    this.status = 'Selección de ubicación cancelada.';
  }

  protected useGuardCurrentLocation(): void {
    if (!this.isGuardAccess() || this.guardGeolocationLoading) {
      return;
    }

    if (!navigator.geolocation) {
      this.setGuardGeolocationError('Este navegador no permite obtener la ubicación del dispositivo.');
      return;
    }

    this.guardFieldPointSelectionActive = false;
    this.guardGeolocationLoading = true;
    this.guardGeolocationError = '';
    this.status = 'Obteniendo la ubicación actual…';

    navigator.geolocation.getCurrentPosition(
      (position) => {
        void this.captureGuardGeolocation(position.coords.longitude, position.coords.latitude);
      },
      (error) => {
        this.guardGeolocationLoading = false;
        const message = error.code === error.PERMISSION_DENIED
          ? 'No se ha concedido permiso para acceder a la ubicación.'
          : error.code === error.POSITION_UNAVAILABLE
            ? 'No se ha podido determinar la ubicación actual.'
            : error.code === error.TIMEOUT
              ? 'La localización ha tardado demasiado. Inténtalo de nuevo.'
              : 'No se ha podido obtener la ubicación actual.';
        this.setGuardGeolocationError(message);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }

  protected resetGuardSelectedMapPoint(): void {
    this.guardSelectedMapPoint = undefined;
    this.guardDetectedMapElements = [];
    this.guardGeolocationError = '';
    this.guardFieldPointLayer?.getSource()?.clear();
  }

  protected handleGuardSessionExpired(): void {
    this.closeAuthenticatedSession();
  }

  protected selectGuardTake(take: GuardTake): void {
    this.selectedGuardTakeId = take.id;
    this.selectedIrrigatorParcelId = '';
    this.guardTakeStatusError = '';
    this.viewerState.showFeatureInfoPanel(take.panelData);
    this.refreshMapSize();
    this.status = 'Información de toma consultada.';
  }

  protected get selectedGuardTake(): GuardTake | undefined {
    return this.guardTakes.find((take) => take.id === this.selectedGuardTakeId);
  }

  protected async updateGuardTakeStatus(request: GuardTakeStatusChangeRequest): Promise<void> {
    const take = this.selectedGuardTake;
    if (!take || this.guardTakeStatusSaving) {
      return;
    }

    this.guardTakeStatusSaving = true;
    this.guardTakeStatusError = '';

    const result = await this.guardTakesService.updateStatus(
      take,
      request.status,
      this.getAuthorizationHeaders()
    );

    this.guardTakeStatusSaving = false;

    if (result.kind === 'unauthorized') {
      this.closeAuthenticatedSession();
      return;
    }

    if (result.kind === 'invalid') {
      this.guardTakeStatusError = result.message;
      this.status = result.message;
      return;
    }

    if (result.kind === 'unavailable') {
      this.guardTakeStatusError = 'No se ha podido guardar la operación. Inténtalo de nuevo.';
      this.status = this.guardTakeStatusError;
      return;
    }

    this.guardTakes = this.guardTakes.map((currentTake) =>
      currentTake.backendId === result.take.backendId ? result.take : currentTake
    );
    this.selectedGuardTakeId = result.take.id;
    this.viewerState.showFeatureInfoPanel(result.take.panelData);
    this.refreshGuardStatusLayers();
    this.status = `Toma ${result.take.label} marcada como ${request.status}.`;
  }

  private async ensureGuardTakesLoaded(force = false): Promise<void> {
    if (!this.isGuardAccess() || this.guardTakesLoading || (!force && this.guardTakesLoaded)) {
      return;
    }

    this.guardTakesLoading = true;
    this.guardTakesError = '';

    const result = await this.guardTakesService.getCurrentTakes(this.getAuthorizationHeaders());

    this.guardTakesLoading = false;

    if (result.kind === 'unauthorized') {
      this.closeAuthenticatedSession();
      return;
    }

    if (result.kind === 'unavailable') {
      this.guardTakesError = 'No se han podido cargar tus tomas.';
      this.status = this.guardTakesError;
      return;
    }

    this.guardTakes = result.takes;
    this.guardTakesLoaded = true;
    this.status = result.takes.length
      ? `Tomas cargadas: ${result.takes.length}.`
      : 'No se han encontrado tomas asociadas al guarda.';
  }

  private async fitCurrentIrrigatorExtent(force = false): Promise<void> {
    if (!this.map || !this.isIrrigatorAccess() || (!force && this.hasFitIrrigatorExtent)) {
      return;
    }

    const result = await this.irrigatorExtentService.getBufferedCurrentExtent(this.getAuthorizationHeaders());

    if (result.kind === 'empty') {
      this.status = 'No se ha encontrado geometria asociada al comunero.';
      this.hasFitIrrigatorExtent = true;
      return;
    }

    if (result.kind === 'unauthorized') {
      this.closeAuthenticatedSession();
      return;
    }

    if (result.kind === 'ready') {
      this.map.getView().fit(result.extent, {
        padding: [96, 420, 180, 96],
        duration: 550,
        minResolution: AppComponent.IRRIGATOR_AUTO_FIT_MIN_RESOLUTION
      });
      this.hasFitIrrigatorExtent = true;
      return;
    }

    this.status = 'No se ha podido encuadrar automáticamente la información del comunero.';
  }

  private async fitCurrentGuardExtent(force = false): Promise<void> {
    if (!this.map || !this.isGuardAccess() || (!force && this.hasFitGuardExtent)) {
      return;
    }

    const result = await this.guardExtentService.getBufferedCurrentExtent(this.getAuthorizationHeaders());

    if (result.kind === 'empty') {
      this.status = 'No se ha encontrado geometria asociada a las acequias del guarda.';
      this.hasFitGuardExtent = true;
      return;
    }

    if (result.kind === 'unauthorized') {
      this.closeAuthenticatedSession();
      return;
    }

    if (result.kind === 'ready') {
      this.map.getView().fit(result.extent, {
        padding: [96, 420, 180, 96],
        duration: 550,
        minResolution: AppComponent.IRRIGATOR_AUTO_FIT_MIN_RESOLUTION
      });
      this.hasFitGuardExtent = true;
      return;
    }

    this.status = 'No se ha podido encuadrar automáticamente la información del guarda.';
  }

  private getBufferedMapExtent(extent: [number, number, number, number]): [number, number, number, number] {
    const width = extent[2] - extent[0];
    const height = extent[3] - extent[1];
    const buffer = Math.max(width, height, 80) * 0.28;

    return [
      extent[0] - buffer,
      extent[1] - buffer,
      extent[2] + buffer,
      extent[3] + buffer
    ];
  }

  private registerMapClickHandler(): void {
    if (!this.map) {
      return;
    }

    this.map.on('singleclick', async (event: any) => {
      if (this.guardFieldPointSelectionActive && this.isGuardAccess()) {
        await this.captureGuardFieldPoint(event.coordinate);
        return;
      }

      if (!this.featureInfoActive) {
        return;
      }

      const queries = this.getFeatureInfoQueries();

      if (!queries.length) {
        this.status = 'Activa primero una capa para consultar información.';
        return;
      }

      await this.loadFeatureInfo(event.coordinate, queries);
    });
  }

  private async captureGuardGeolocation(longitude: number, latitude: number): Promise<void> {
    if (!this.map || !window.ol || !this.isGuardAccess()) {
      this.guardGeolocationLoading = false;
      this.setGuardGeolocationError('El mapa no está disponible para seleccionar la ubicación.');
      return;
    }

    const view = this.map.getView();
    const coordinate = window.ol.proj.transform(
      [longitude, latitude],
      'EPSG:4326',
      view.getProjection()
    );
    view.animate({ center: coordinate, duration: 450 });
    try {
      await this.captureGuardFieldPoint(coordinate, 'geolocation');
    } finally {
      this.guardGeolocationLoading = false;
    }
  }

  private setGuardGeolocationError(message: string): void {
    this.guardGeolocationError = message;
    this.status = message;
  }

  private async captureGuardFieldPoint(
    coordinate: number[],
    source: 'map' | 'geolocation' = 'map'
  ): Promise<void> {
    if (!this.map || !window.ol || coordinate.length < 2) {
      return;
    }

    const [longitude, latitude] = window.ol.proj.toLonLat(coordinate);
    const [x, y] = this.projectLonLatToUtm30N(longitude, latitude);
    this.guardSelectedMapPoint = { x, y, srid: 25830 };
    this.showGuardFieldPointMarker(coordinate);

    const detectedElements = await this.detectGuardFieldElements(coordinate);
    if (source === 'map' && !this.guardFieldPointSelectionActive) {
      return;
    }

    this.guardDetectedMapElements = detectedElements;
    this.guardFieldPointSelectionActive = false;
    const origin = source === 'geolocation' ? 'Ubicación actual seleccionada' : 'Punto seleccionado';
    this.status = detectedElements.length
      ? `${origin} y ${detectedElements.length} elemento(s) identificado(s).`
      : `${origin}. Elige manualmente el elemento relacionado.`;
  }

  private async detectGuardFieldElements(coordinate: number[]): Promise<GuardFieldElement[]> {
    if (!this.map || !window.ol) {
      return [];
    }

    const relevantQueries = this.getFeatureInfoQueries().filter((query) =>
      Boolean(this.getGuardFieldElementType(query.proxyKey))
    );
    const view = this.map.getView();
    const resolution = view.getResolution();
    if (!resolution) {
      return [];
    }

    const responses = await Promise.all(relevantQueries.map(async (query) => {
      const rawInfoUrl = query.source.getFeatureInfoUrl(
        coordinate,
        resolution,
        view.getProjection(),
        { INFO_FORMAT: 'application/json', FEATURE_COUNT: 5 }
      );
      const infoUrl = this.ramWmsService.getControlledFeatureInfoUrl(rawInfoUrl, query.proxyKey);
      if (!infoUrl) {
        return [];
      }

      try {
        const response = await fetch(infoUrl, {
          headers: { Accept: 'application/json', ...this.getAuthorizationHeaders() }
        });
        if (response.status === 401 || response.status === 403) {
          this.closeAuthenticatedSession();
          return [];
        }
        if (!response.ok) {
          return [];
        }

        const type = this.getGuardFieldElementType(query.proxyKey);
        return type
          ? this.ramWmsService.getFeatureInfoItems(await response.json())
              .map((feature) => this.createGuardFieldElement(type, feature))
              .filter((element): element is GuardFieldElement => Boolean(element))
          : [];
      } catch {
        return [];
      }
    }));

    const elements: GuardFieldElement[] = [];
    responses.flat().forEach((element) => {
      if (!elements.some((candidate) => candidate.type === element.type && candidate.id === element.id)) {
        elements.push(element);
      }
    });
    return elements;
  }

  private getGuardFieldElementType(proxyKey: string): GuardFieldElementType | undefined {
    const elementTypes: Record<string, GuardFieldElementType> = {
      'acequia-principal': 'Canal',
      'acequias-secundarias': 'SecondaryCanal',
      parcelas: 'Parcel',
      'parcelas-guardas': 'Parcel',
      tomas: 'Take',
      'tomas-guardas': 'Take',
      'tomas-abiertas': 'Take'
    };
    return elementTypes[proxyKey];
  }

  private createGuardFieldElement(type: GuardFieldElementType, feature: RamFeatureInfoItem): GuardFieldElement | undefined {
    const id = this.getFieldElementId(type, feature);
    if (!id) {
      return undefined;
    }

    const label = this.getFieldElementProperty(feature.properties, this.getFieldElementLabelAliases(type)) ||
      this.getDefaultFieldElementLabel(type, id);
    return { type, id, label };
  }

  private getFieldElementId(type: GuardFieldElementType, feature: RamFeatureInfoItem): number | undefined {
    const typeAliases: Record<GuardFieldElementType, string[]> = {
      Canal: ['id_acequia', 'id_acequias', 'acequia_id'],
      SecondaryCanal: ['id_acequia', 'id_acequias', 'acequia_id'],
      Take: ['id_toma', 'numero_toma', 'num_toma', 'n_toma', 'cod_toma', 'toma_numero'],
      Parcel: ['parcela', 'id_parcela', 'parcela_id', 'id_finca']
    };
    const propertyValue = this.getFieldElementProperty(feature.properties, [
      ...typeAliases[type],
      'id',
      'gid',
      'objectid'
    ]);
    const propertyId = Number(propertyValue);
    if (Number.isInteger(propertyId) && propertyId > 0) {
      return propertyId;
    }

    const featureIdMatch = `${feature.id ?? ''}`.match(/(\d+)$/);
    const featureId = featureIdMatch ? Number(featureIdMatch[1]) : Number.NaN;
    return Number.isInteger(featureId) && featureId > 0 ? featureId : undefined;
  }

  private getFieldElementLabelAliases(type: GuardFieldElementType): string[] {
    if (type === 'Take') {
      return ['nom_toma', 'nombre_toma', 'toma_asociada', 'toma', 'numero_toma', 'num_toma'];
    }
    if (type === 'Parcel') {
      return ['referenciacatastral', 'referencia_catastral', 'ref_catastral', 'refcat', 'parcela'];
    }
    return ['nombre', 'name', 'denominacion', 'acequia', 'nom_acequia'];
  }

  private getFieldElementProperty(properties: Record<string, unknown>, aliases: string[]): string {
    const normalizedAliases = aliases.map((alias) => this.normalizeFieldElementProperty(alias));
    const entry = Object.entries(properties).find(([key, value]) =>
      value !== undefined && value !== null && `${value}`.trim() &&
      normalizedAliases.includes(this.normalizeFieldElementProperty(key))
    );
    return entry ? `${entry[1]}`.trim() : '';
  }

  private normalizeFieldElementProperty(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  }

  private getDefaultFieldElementLabel(type: GuardFieldElementType, id: number): string {
    const labels: Record<GuardFieldElementType, string> = {
      Canal: 'Acequia',
      SecondaryCanal: 'Acequia secundaria',
      Take: 'Toma',
      Parcel: 'Parcela'
    };
    return `${labels[type]} nº${id}`;
  }

  private projectLonLatToUtm30N(longitude: number, latitude: number): [number, number] {
    const semiMajorAxis = 6378137;
    const eccentricitySquared = 0.00669438002290;
    const scaleFactor = 0.9996;
    const latitudeRadians = latitude * Math.PI / 180;
    const longitudeRadians = longitude * Math.PI / 180;
    const centralMeridian = -3 * Math.PI / 180;
    const secondEccentricitySquared = eccentricitySquared / (1 - eccentricitySquared);
    const radius = semiMajorAxis / Math.sqrt(1 - eccentricitySquared * Math.sin(latitudeRadians) ** 2);
    const tangentSquared = Math.tan(latitudeRadians) ** 2;
    const etaSquared = secondEccentricitySquared * Math.cos(latitudeRadians) ** 2;
    const longitudeTerm = Math.cos(latitudeRadians) * (longitudeRadians - centralMeridian);
    const meridianArc = semiMajorAxis * (
      (1 - eccentricitySquared / 4 - 3 * eccentricitySquared ** 2 / 64 - 5 * eccentricitySquared ** 3 / 256) * latitudeRadians
      - (3 * eccentricitySquared / 8 + 3 * eccentricitySquared ** 2 / 32 + 45 * eccentricitySquared ** 3 / 1024) * Math.sin(2 * latitudeRadians)
      + (15 * eccentricitySquared ** 2 / 256 + 45 * eccentricitySquared ** 3 / 1024) * Math.sin(4 * latitudeRadians)
      - (35 * eccentricitySquared ** 3 / 3072) * Math.sin(6 * latitudeRadians)
    );
    const x = scaleFactor * radius * (
      longitudeTerm + (1 - tangentSquared + etaSquared) * longitudeTerm ** 3 / 6
      + (5 - 18 * tangentSquared + tangentSquared ** 2 + 72 * etaSquared - 58 * secondEccentricitySquared) * longitudeTerm ** 5 / 120
    ) + 500000;
    const y = scaleFactor * (
      meridianArc + radius * Math.tan(latitudeRadians) * (
        longitudeTerm ** 2 / 2
        + (5 - tangentSquared + 9 * etaSquared + 4 * etaSquared ** 2) * longitudeTerm ** 4 / 24
        + (61 - 58 * tangentSquared + tangentSquared ** 2 + 600 * etaSquared - 330 * secondEccentricitySquared) * longitudeTerm ** 6 / 720
      )
    );
    return [x, y];
  }

  private showGuardFieldPointMarker(coordinate: number[]): void {
    if (!this.map || !window.ol) {
      return;
    }

    if (!this.guardFieldPointLayer) {
      this.guardFieldPointLayer = new window.ol.layer.Vector({
        source: new window.ol.source.Vector(),
        zIndex: 200,
        style: new window.ol.style.Style({
          image: new window.ol.style.Circle({
            radius: 9,
            fill: new window.ol.style.Fill({ color: '#ffffff' }),
            stroke: new window.ol.style.Stroke({ color: '#16753c', width: 4 })
          })
        })
      });
      this.map.addLayer(this.guardFieldPointLayer);
    }

    const source = this.guardFieldPointLayer.getSource();
    source.clear();
    source.addFeature(new window.ol.Feature(new window.ol.geom.Point(coordinate)));
  }

  private async loadFeatureInfo(coordinate: number[], queries: FeatureInfoQuery[]): Promise<void> {
    if (!this.map || !window.ol) {
      return;
    }

    const view = this.map.getView();
    const resolution = view.getResolution();

    if (!resolution) {
      return;
    }

    const infoRequests = queries
      .map((query) => {
        const rawInfoUrl = query.source.getFeatureInfoUrl(
          coordinate,
          resolution,
          view.getProjection(),
          {
            INFO_FORMAT: 'application/json'
          }
        );

        return {
          ...query,
          infoUrl: this.ramWmsService.getControlledFeatureInfoUrl(rawInfoUrl, query.proxyKey)
        };
      })
      .filter((query): query is FeatureInfoQuery & { infoUrl: string } => Boolean(query.infoUrl));

    if (!infoRequests.length) {
      this.status = 'No se pudo construir la peticion GetFeatureInfo para las capas activas.';
      return;
    }

    const responses = await Promise.all(
      infoRequests.map(async (query) => {
        try {
          const response = await fetch(query.infoUrl, {
            headers: {
              Accept: 'application/json',
              ...this.getAuthorizationHeaders()
            }
          });

          if (!response.ok) {
            if (response.status === 401) {
              this.closeAuthenticatedSession();
              return undefined;
            }

            throw new Error(`HTTP ${response.status}`);
          }

          const properties = this.ramWmsService.getFeatureProperties(await response.json());
          return {
            proxyKey: query.proxyKey,
            properties
          };
        } catch {
          return undefined;
        }
      })
    );

    if (!this.featureInfoActive) {
      return;
    }

    const featureResponses = responses
      .filter((response): response is { proxyKey: string; properties: Record<string, unknown>[] } => Boolean(response));
    const featureProperties = featureResponses
      .flatMap((response) => response.properties);
    const isOwnIrrigatorParcel = featureResponses.some((response) =>
      response.proxyKey === 'parcelas-regantes' && response.properties.length > 0
    );
    const panelData = this.featureInfoPanelDataService.create(
      featureProperties,
      this.isIrrigatorAccess(),
      isOwnIrrigatorParcel
    );

    if (!panelData) {
      this.closePopup();
      this.status = 'No hay información para mostrar en este punto.';
      return;
    }

    this.selectedIrrigatorParcelId = '';
    this.selectedGuardTakeId = '';
    this.guardTakeStatusError = '';
    this.viewerState.showFeatureInfoPanel(panelData);
    this.refreshMapSize();
    this.status = 'Información de parcela consultada.';
  }

  private getFeatureInfoQueries(): FeatureInfoQuery[] {
    const queries = this.getAvailableRamLayers()
      .filter((ramLayer) =>
        ramLayer.enabled &&
        ramLayer.mapLayer &&
        ramLayer.proxyKey !== RAM_ORTHOPHOTO_LAYER_KEY
      )
      .map((ramLayer) => ({
        label: ramLayer.label,
        proxyKey: ramLayer.proxyKey,
        source: ramLayer.mapLayer.getSource()
      }));

    return queries;
  }

  protected getAvailableRamLayers(): RamLayerOption[] {
    return this.ramLayers.filter((ramLayer) =>
      (!ramLayer.requiredProfile || ramLayer.requiredProfile === this.currentProfile) &&
      (!ramLayer.requiredProfiles || (this.currentProfile !== 'public' && ramLayer.requiredProfiles.includes(this.currentProfile)))
    );
  }

  private refreshMapSize(): void {
    window.setTimeout(() => this.map?.updateSize(), 0);
    window.setTimeout(() => this.map?.updateSize(), 120);
    window.setTimeout(() => this.map?.updateSize(), 320);
  }

  private refreshGuardStatusLayers(): void {
    this.getAvailableRamLayers()
      .filter((ramLayer) => ramLayer.mapLayer && ramLayer.proxyKey !== RAM_ORTHOPHOTO_LAYER_KEY)
      .forEach((ramLayer) => ramLayer.mapLayer.getSource().refresh());
  }

  private getNormalizedCadastralReferenceSearch(): string {
    return this.cadastralReferenceSearch.trim().toUpperCase();
  }

  private getAuthorizationHeaders(): Record<string, string> {
    return this.authService.getAuthorizationHeaders(() => this.closeAuthenticatedSession());
  }

  private resetIrrigatorParcels(): void {
    this.irrigatorParcels = [];
    this.irrigatorParcelsLoading = false;
    this.irrigatorParcelsError = '';
    this.selectedIrrigatorParcelId = '';
    this.irrigatorParcelsLoaded = false;
  }

  private resetGuardTakes(): void {
    this.guardTakes = [];
    this.guardTakesLoading = false;
    this.guardTakesError = '';
    this.selectedGuardTakeId = '';
    this.guardTakeStatusSaving = false;
    this.guardTakeStatusError = '';
    this.guardTakesLoaded = false;
  }

  private resetGuardFieldOperations(): void {
    this.guardFieldOperationsView = null;
    this.incidentsSummaryVisible = false;
    this.maintenanceSummaryVisible = false;
    this.guardFieldPointSelectionActive = false;
    this.resetGuardSelectedMapPoint();
    if (this.guardFieldPointLayer && this.map) {
      this.map.removeLayer(this.guardFieldPointLayer);
    }
    this.guardFieldPointLayer = undefined;
  }

}
